<script lang="tsx">
  import { computed, defineComponent, getCurrentInstance, type PropType } from "vue";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faAngleDown, faAngleUp, faSearch, faLanguage, faUser, faBars } from "@fortawesome/free-solid-svg-icons";

  import {
    vIntersectionObserver as intersectionObserver, vElementBounding as elementBounding,
    vElementSize as elementSize,
  } from "@vueuse/components";

  import { useCssVar, type ElementSize, type UseElementBoundingReturn } from "@vueuse/core";
  // this type is not exported :(
  type ElementBounding = Omit<UseElementBoundingReturn, "update">;


  import { Button, Skeleton } from "primevue";


  const Navbar = defineComponent({
    components: {
      Button, Skeleton,
    },
    directives: { intersectionObserver, elementBounding, elementSize },
    props: {},
    setup() {
      return {
        css: {
          // height of the first grid row; the available height for our logo
          firstRowHeight: useCssVar("--navbar-first-row-height"),
        }
      }
    },
    data() { return {
      bounds: undefined as ElementBounding | undefined,

      faAngleDown, faAngleUp, faSearch, faLanguage, faUser, faBars
    }},

    methods: {
      onBounding(bounds: ElementBounding) {
        this.$data.bounds = bounds;
      },
    },
  });

  export default Navbar;
</script>

<template>
  <!-- ensure the button stylesheet gets loaded -->
  <div v-show="false"><Button/></div>

  <nav
    v-element-bounding="[
      onBounding,
      {immediate: true, windowScroll: false, windowResize: true, updateTiming: 'next-frame'}
    ]">
    <a class="logo">
      <!-- <img :src="global.logo[0]!.url ?? undefined"
        :title="global.logo[0]!.title ?? undefined" :alt="global.logo[0]!.alt ?? undefined"
        :width="global.logo[0]!.width ?? undefined" :height="global.logo[0]!.height ?? undefined" /> -->
    </a>

    <a class="title">
      <!-- <img :src="global.header[0]!.url ?? undefined"
        :title="global.header[0]!.title ?? undefined" :alt="global.header[0]!.alt ?? undefined"
        :width="global.header[0]!.width ?? undefined" :height="global.header[0]!.height ?? undefined" /> -->
    </a>
    <div class="title-measure" v-element-size="({height}: ElementSize) => css.firstRowHeight.value = height + 'px'"></div>


    <ul class="linktree">
      <li class="short">
        <Skeleton class="skeleton-button" style="width: 8ch"/>
      </li>

      <!-- eslint-disable-next-line vue/no-unused-vars -->
      <li class="long" v-for="size in [7, 15, 10, 11]">
        <Skeleton class="skeleton-button" :style="{width: `${size+2}ch`}"/>
      </li>

      <li class="rest">
        <Skeleton class="skeleton-button" style="width: var(--p-button-icon-only-width)"/>
        <Skeleton class="skeleton-button" style="width: var(--p-button-icon-only-width)"/>
      </li>
    </ul>
  </nav>
</template>

<style lang="less" scoped>
  @import (reference) "@/style/main.less";

  :global(:root) {
    // set from script: --navbar-first-row-height,
    // --navbar--logo-aspect-ratio.

    --navbar-row-height: calc((var(--p-button-padding-y) + 0.5em) * 2 + 1em * var(--line-height));
  }

  .skeleton-button {
    padding: var(--p-button-padding-x) var(--p-button-padding-y);
    height: calc(1em * var(--line-height) + var(--p-button-padding-y) * 2) !important;
    display: inline-block;
  }

  nav {
    background-color: var(--p-primary-50);

    // border-block-end: 1px solid var(--color-border);
    // border-inline: 1px solid var(--color-border);
    border-radius: 0 0 var(--spacing-radius) var(--spacing-radius);
    box-shadow:
      0 var(--spacing-shadow) var(--spacing-shadow) 0 var(--color-shadow),
      calc(var(--spacing-shadow) * -1) 0 var(--spacing-shadow) 0 var(--color-shadow),
      var(--spacing-shadow) 0 var(--spacing-shadow) 0 var(--color-shadow);

    height: 100%;
    width: 100%;

    display: grid;
    grid:
      "logo title title" max-content
      "linktree linktree linktree" var(--navbar-row-height)
      "linktree linktree linktree" max-content
      / fit-content(10em) 1fr 10em;
    align-items: center;
    justify-content: center;

    @media (width < @screen-width-small) {
      grid:
        "title title title" max-content
        "linktree linktree linktree" var(--navbar-row-height)
        "linktree linktree linktree" max-content
        / fit-content(10em) 1fr 10em;

      & > .logo { display: none; }
    }

    & > .logo { grid-area: logo; }
    & > .logo-measure { grid-area: logo; }
    & > .logo > img { grid-area: logo; }
    & > .title { grid-area: title; }
    & > .title-measure { grid-area: title; }
    & > .search { grid-area: search; }
    & > .linktree { grid-area: linktree; }

    // ensure that the border radius is clipped on the image
    & > .logo {
      &, & * {
        border-end-start-radius: var(--spacing-radius);
      }
    }

    & > .linktree {
      padding-block: 0.5em;
      background-color: var(--p-secondary-800);
      border-radius: 0 0 var(--spacing-radius) var(--spacing-radius);
    }
  }


  @media screen and (width >= @screen-width-small) {
    .logo { height: 8em; }
    .title-measure { height: 8em; }
  }

  .title, .title-measure {
    aspect-ratio: calc(535 / 74);
  }


  .logo-measure, .title-measure {
    position: relative;
    align-self: stretch;
    pointer-events: none;
  }

  .title {
    margin-block-start: var(--spacing-inline);
    margin-inline: var(--spacing-block);
    img {
      max-width: 100%;
      height: auto;
    }

    justify-self: flex-start;
  }

  .linktree {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: start;
    justify-content: center;
    padding-inline: var(--spacing-inline);
    gap: var(--spacing-inline);

    & > * { flex: 0 0 auto; }

    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -webkit-user-select: none;

    // filler to start-align last row
    &::after {
      content: "";
      flex: 1 0 auto;
      min-width: 0;
    }

    & .text {
      --p-button-padding-x: 0.25em;
      --p-button-link-color: var(--p-neutral-50);
      --p-button-link-hover-color: var(--p-neutral-50);
      --p-button-link-active-color: var(--p-neutral-300);
    }

    & > .long {
      display: contents;
      @media screen and (width < @screen-width-small) {
        display: none;
      }
    }

    & > .short {
      display: contents;
      @media screen and (width >= @screen-width-small) {
        display: none;
      }
    }

    & > .rest {
      display: contents;
    }
  }
</style>
